import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Competence, { CompetenceItem } from "../../../models/Competence";
import AppText, { FormLabel } from "../../Core/AppText/AppText";

type SelectedCompetence = {
	branch: string;
	service?: string;
	skills?: string[];
};

export interface ICompetencePillsProps {
	/**
	 * The selected competence
	 */
	value?: Competence;

	/**
	 * Callback invoked when competence value is changed
	 */
	onChange?: (val: SelectedCompetence) => any;

	/**
	 * List of all competences for an organisation.
	 * This is not the selected value.
	 */
	competences: CompetenceItem[];

	/**
	 * Optional competence that is highlighted.
	 *
	 * The highlighted competence will have a different border color
	 * to make values stand out.
	 */
	highlightedCompetence?: Competence;

	errors?: any;

	/**
	 * Optional array of services that are enabled. This can be used i.e. when
	 * only enabling competence that is available within a contract.
	 *
	 * If not set all items will be enabled.
	 */
	enabledServices?: { branch: string; service: string }[];

	/**
	 * If to hide form label
	 */
	hideLabel?: boolean;

	/**
	 * If to hide branch selection. This can only be used when a value is provided.
	 */
	hideBranch?: boolean;
}

const CompetencePills: React.FC<ICompetencePillsProps> = ({
	value,
	onChange,
	competences,
	errors,
	enabledServices,
	hideLabel,
	hideBranch,
	highlightedCompetence,
}) => {
	const { t } = useTranslation();
	const selection = useMemo(() => getSelectedCompetence(competences, value), [competences, value]);
	const highlight = useMemo(
		() => getSelectedCompetence(competences, highlightedCompetence),
		[competences, highlightedCompetence]
	);

	const [branch, setBranch] = useState<CompetenceItem | undefined>(selection.branch);
	const [service, setService] = useState<CompetenceItem | undefined>(selection.service);
	const [skills, setSkills] = useState<string[] | undefined>(selection.skills);

	const handleIndustryClick = (item: CompetenceItem) => {
		setBranch(item);
		setService(undefined);
		setSkills([]);
		setCompetence(item.key);
	};

	const handleServiceClick = (item: CompetenceItem) => {
		setService(item);
		setSkills([]);

		if (branch) setCompetence(branch.key, item.key);
	};

	const handleSkillClick = (item: CompetenceItem) => {
		let newSkills: string[] = [];

		if (skills) {
			const i = skills.indexOf(item.key);

			if (i > -1) {
				newSkills = skills.filter((s) => s !== item.key);
			} else {
				newSkills = [...skills, item.key];
			}
		}

		setSkills(newSkills);

		if (branch && service) setCompetence(branch.key, service.key, newSkills);
	};

	const setCompetence = (
		selectedBranch: string,
		selectedService?: string,
		selectedSkills?: string[]
	) => {
		let competence: SelectedCompetence = { branch: selectedBranch };

		if (selectedService) competence.service = selectedService;
		if (selectedSkills) competence.skills = selectedSkills;

		if (onChange) onChange(competence);
	};

	return (
		<CompetencePillsStyles>
			{!hideLabel && <FormLabel margin="1rem 0">{t("COMPETENCE")} *</FormLabel>}

			<div>
				{!hideBranch && (
					<div className="row level-1">
						{competences.map((item) => {
							return (
								<div
									className={`pill ${item.key === branch?.key && "active"} ${
										item.key === highlight.branch?.key && "highlighted"
									}`}
									key={item.key}
									data-testid={`competence-branch-${item.key}`}
									onClick={() => handleIndustryClick(item)}
								>
									{item.name.sv}{" "}
									<span className="num-children">
										{!!item.items?.length && `(${item.items.length})`}
									</span>
								</div>
							);
						})}
						{errors && (
							<AppText margin="1rem 0 0 0" size="xs" color="danger">
								{errors.branch && t(errors.branch.message as any)}
							</AppText>
						)}
					</div>
				)}

				{branch && (
					<div className="row level-2">
						{(branch.items || []).map((item) => {
							const disabled =
								enabledServices &&
								!enabledServices.find(
									(enabledService) =>
										branch.key === enabledService.branch && item?.key === enabledService.service
								);

							return (
								<div
									className={`pill ${item.key === service?.key && "active"} ${
										disabled && "disabled"
									} ${item.key === highlight.service?.key && "highlighted"}`}
									key={item.key}
									data-testid={`competence-service-${item.key}`}
									onClick={() => handleServiceClick(item)}
								>
									{item.name.sv} {!!item.items?.length && `(${item.items.length})`}
								</div>
							);
						})}
					</div>
				)}
				{errors && (
					<AppText margin="0.5rem 0 0 0" size="xs" color="danger">
						{errors.service && t(errors.service.message as any)}
					</AppText>
				)}

				{!!service?.items?.length && (
					<div className="row level-3">
						{service.items
							.slice()
							.sort((a, b) => a.name.sv!.localeCompare(b.name.sv!))
							.map((item) => {
								return (
									<div
										className={`pill ${skills?.includes(item.key) && "active"} ${
											highlight.skills?.includes(item.key) && "highlighted"
										}`}
										key={item.key}
										data-testid={`competence-skill-${item.key}`}
										onClick={() => handleSkillClick(item)}
									>
										{item.name.sv}
										{!!item.items?.length && `(${item.items.length})`}
									</div>
								);
							})}
					</div>
				)}
			</div>
		</CompetencePillsStyles>
	);
};

export default CompetencePills;

const CompetencePillsStyles = styled.div`
	max-width: 700px;
	.row {
		margin-bottom: 0.5rem;
		&:last-child {
			margin-bottom: 0.4rem;
		}
	}
	.pill {
		display: inline-block;
		background-color: white;
		border-radius: 2rem;
		padding: 0.4rem 1rem;
		margin-right: 0.4rem;
		border: 2px solid ${(props) => props.theme.colors.COLOR_GRAY};
		cursor: pointer;
		user-select: none;
		margin-bottom: 0.4rem;
		font-size: 1.1rem;
		line-height: 100%;
		&:hover:not(.active) {
			opacity: 0.75;
			background-color: ${(props) => props.theme.colors.COLOR_HOVER_ROW};
		}
		&.active {
			background-color: ${(props) => props.theme.colors.COLOR_ACTION_BLUE};
			border-color: ${(props) => props.theme.colors.COLOR_ACTION_BLUE};
			color: white;
		}
		.num-children {
			font-size: 90%;
		}
		&.disabled {
			color: ${(props) => props.theme.colors.COLOR_GRAY};
			pointer-events: none;
		}
	}
	.level-2 .pill {
		font-size: 1rem;

		&.active {
			background-color: ${(props) => props.theme.colors.COLOR_BLACK};
			border-color: ${(props) => props.theme.colors.COLOR_BLACK};
		}
	}
	.level-3 .pill {
		font-size: 0.9rem;

		&.active {
			background-color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT_DARK};
			border-color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT_DARK};
		}
	}

	.highlighted {
		border-color: ${(props) => props.theme.colors.COLOR_ORANGE} !important;
	}
`;

function getSelectedCompetence(competences: CompetenceItem[], selectedCompetence?: Competence) {
	const competence: {
		branch?: CompetenceItem;
		service?: CompetenceItem;
		skills?: string[];
	} = {
		branch: undefined,
		service: undefined,
		skills: [],
	};

	if (!selectedCompetence) return competence;

	competence.branch = competences.find((c) => c.key === selectedCompetence.branch);

	if (competence.branch) {
		competence.service = (competence.branch.items || []).find(
			(c) => c.key === selectedCompetence.service
		);

		if (competence.service?.items && selectedCompetence.skills?.length) {
			competence.skills = competence.service.items
				.filter((c) => selectedCompetence.skills?.includes(c.key))
				.map((c) => c.key);
		}
	}

	return competence;
}
